import { Customer, ItsmConfig } from '../../../types';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { OPERATIONS_ACCOUNT_REGION } from '../../../shared/constants/aws';
import { useAuth } from '../../auth/context/AuthContext';
import { useMemo } from 'react';
import type { FreshserviceEntity } from '../../../shared/components/FreshserviceEntityPicker';
import type { ItsmEntityRef } from '../../../types/backend';

export type { FreshserviceEntity };

export interface OperationsRoleTestResult {
  success: boolean;
  message?: string;
  error?: string;
  details?: Record<string, unknown>;
}

export interface ItsmConfigTestResult {
  success: boolean;
  message: string;
  company_id?: number;
}

export const createCustomerService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    getCustomers: async (): Promise<Customer[]> => {
      return rpcCall<Customer[]>(client, 'customers.ListCustomers');
    },

    getCustomerById: async (customerId: string): Promise<Customer> => {
      return rpcCall(client, 'customers.GetCustomer', { customer_id: customerId });
    },

    createCustomer: async (customer: Omit<Customer, 'id' | 'created_at' | 'updated_at'>): Promise<Customer> => {
      return rpcCall(client, 'customers.CreateCustomer', customer as Record<string, unknown>);
    },

    updateCustomer: async (customerId: string, customer: Partial<Customer>): Promise<Customer> => {
      return rpcCall(client, 'customers.UpdateCustomer', { customer_id: customerId, ...customer });
    },

    deleteCustomer: async (customerId: string): Promise<void> => {
      await rpcCall(client, 'customers.DeleteCustomer', { customer_id: customerId });
    },

    updateItsmConfig: async (customerId: string, itsmConfig: ItsmConfig | null): Promise<Customer> => {
      return rpcCall(client, 'customers.UpdateItsmConfig', {
        customer_id: customerId,
        itsm_config: itsmConfig,
      });
    },

    testItsmConfig: async (customerId: string): Promise<ItsmConfigTestResult> => {
      return rpcCall<ItsmConfigTestResult>(client, 'customers.TestItsmConfig', {
        customer_id: customerId,
      });
    },

    listFreshserviceCompanies: async (customerId: string): Promise<FreshserviceEntity[]> => {
      return rpcCall<FreshserviceEntity[]>(client, 'customers.ListFreshserviceCompanies', {
        customer_id: customerId,
      });
    },

    listFreshserviceGroups: async (customerId: string): Promise<FreshserviceEntity[]> => {
      return rpcCall<FreshserviceEntity[]>(client, 'customers.ListFreshserviceGroups', {
        customer_id: customerId,
      });
    },

    listFreshserviceAgents: async (customerId: string): Promise<FreshserviceEntity[]> => {
      return rpcCall<FreshserviceEntity[]>(client, 'customers.ListFreshserviceAgents', {
        customer_id: customerId,
      });
    },

    // The tenant's ticket statuses, custom ones included — the vocabulary a rule's
    // auto-resolve target is chosen from. Typed from the GENERATED ItsmEntityRef, not
    // the hand-written FreshserviceEntity its three siblings assert (see #1245).
    listFreshserviceStatuses: async (customerId: string): Promise<ItsmEntityRef[]> => {
      return rpcCall<ItsmEntityRef[]>(client, 'customers.ListFreshserviceStatuses', {
        customer_id: customerId,
      });
    },

    testOperationsRoleAccess: async (customerId: string, roleType: 'readonly' | 'write' = 'readonly'): Promise<OperationsRoleTestResult> => {
      return rpcCall<OperationsRoleTestResult>(client, 'customers.ValidateOpsRoleAccess', {
        customer_id: customerId,
        role_type: roleType,
        aws_operation: {
          service_name: 'sts',
          operation_name: 'get_caller_identity',
          parameters: {},
          region: OPERATIONS_ACCOUNT_REGION,
        },
      });
    },
  };
};

export const useCustomerService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    return createCustomerService(idToken);
  }, [idToken]);
};
